import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listQuizzes, deleteQuiz, type StoredQuiz } from '../services/quizStorageService';
import QuizCard from './QuizCard';
import ClassroomCarousel from './ClassroomCarousel';

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [userQuizzes, setUserQuizzes] = useState<StoredQuiz[]>([]);
  const [isLoadingQuizzes, setIsLoadingQuizzes] = useState(false);
  const [quizzesError, setQuizzesError] = useState<string | null>(null);

  // Load user's quizzes when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUserQuizzes();
    }
  }, [isAuthenticated]);

  const loadUserQuizzes = async () => {
    setIsLoadingQuizzes(true);
    setQuizzesError(null);
    try {
      const response = await listQuizzes();
      // Sort by updated_at in descending order and take only the first 3
      const sortedQuizzes = response.quizzes.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ).slice(0, 3);
      setUserQuizzes(sortedQuizzes);
    } catch (error) {
      console.error('Failed to load quizzes:', error);
      setQuizzesError('Failed to load your quizzes');
    } finally {
      setIsLoadingQuizzes(false);
    }
  };

  const handleDeleteQuiz = async (quizId: string) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) {
      return;
    }

    try {
      await deleteQuiz(quizId);
      setUserQuizzes(prevQuizzes => prevQuizzes.filter(q => q.quiz_id !== quizId));
    } catch (error) {
      console.error('Failed to delete quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    }
  };

  if (isAuthenticated) {
    const displayName = user?.name || user?.given_name || 'there';
    const today = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <section className="mb-12">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {displayName}
          </h1>
          <p className="text-base text-gray-500">
            {today}
          </p>
        </div>

        {/* Your Quizzes Section */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            Your Quizzes <span className="text-gray-500 font-normal">({userQuizzes.length})</span>
          </h2>
          <button
            onClick={() => navigate('/create-quiz')}
            className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Quiz
          </button>
        </div>

        {/* Quiz List */}
        {isLoadingQuizzes ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="text-gray-600 mt-2">Loading your quizzes...</p>
          </div>
        ) : quizzesError ? (
          <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
            <p className="text-red-600">{quizzesError}</p>
            <button
              onClick={loadUserQuizzes}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : userQuizzes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No quizzes yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first quiz and start building your collection!
            </p>
            <button
              onClick={() => navigate('/create-quiz')}
              className="px-6 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Create Your First Quiz
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {userQuizzes.map((quiz) => (
              <QuizCard
                key={quiz.quiz_id}
                quiz={quiz}
                onDelete={handleDeleteQuiz}
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto py-16">
      <div className="mb-16 text-center px-4">
        {/* Main Headline with gradient accent */}
        <div className="inline-block mb-4">
          <span className="text-orange-500 font-semibold text-lg tracking-wide uppercase">
            QuizCrunch for Educators
          </span>
        </div>
        <h1 className="text-6xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
          AI-Powered Quizzes
          <span className="block bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
            Created in Seconds
          </span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
          Let AI generate engaging, curriculum-aligned quizzes instantly. Just describe your topic,
          and watch as intelligent questions appear—complete with explanations and real-time student engagement.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/create-quiz')}
            className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
            </svg>
            Generate Quiz with AI
          </button>
          <button
            onClick={() => document.querySelector('.carousel-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-white text-gray-700 text-lg font-semibold rounded-lg border-2 border-gray-300 hover:border-orange-500 transition-all hover:shadow-md"
          >
            See AI in Action
          </button>
        </div>

        {/* Trust Indicators - AI-focused */}
        <div className="mt-12 flex flex-wrap justify-center items-center gap-6 text-base text-gray-700">
          <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-full border border-orange-200">
            <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">AI-Powered Generation</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Instant Quiz Creation</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Real-Time Engagement</span>
          </div>
        </div>
      </div>

      {/* Classroom Engagement Carousel */}
      <div className="carousel-section">
        <ClassroomCarousel />
      </div>
    </section>
  );
};

export default Hero;